// Aligns slot points into rows parallel to the nearest property-boundary edge.
export function alignPointsToOutline(points, outline) {
  const keys = Object.keys(points || {});
  if (keys.length < 2 || !Array.isArray(outline) || outline.length < 3) return null;

  const latMid = outline.reduce((a, p) => a + p[0], 0) / outline.length;
  const kx = Math.cos((latMid * Math.PI) / 180) || 1;
  const toXY = ([lat, lng]) => ({ x: lng * kx, y: lat });
  const toLL = ({ x, y }) => [y, x / kx];

  const verts = outline.map(toXY);
  const edges = verts.map((a, i) => {
    const b = verts[(i + 1) % verts.length];
    const len = Math.hypot(b.x - a.x, b.y - a.y) || 1e-12;
    return { a, d: { x: (b.x - a.x) / len, y: (b.y - a.y) / len }, len };
  });

  const items = keys.map((k) => ({ k, p: toXY(points[k]) }));

  // typical nearest-neighbour spacing, used as row-separation threshold
  let nn = Infinity;
  for (let i = 0; i < items.length; i++) {
    for (let j = i + 1; j < items.length; j++) {
      const d = Math.hypot(items[i].p.x - items[j].p.x, items[i].p.y - items[j].p.y);
      if (d < nn) nn = d;
    }
  }
  if (!isFinite(nn) || nn <= 0) return null;

  items.forEach((it) => {
    let best = null;
    edges.forEach((e, ei) => {
      const n = { x: -e.d.y, y: e.d.x };
      const vx = it.p.x - e.a.x, vy = it.p.y - e.a.y;
      const s = vx * e.d.x + vy * e.d.y;
      const t = vx * n.x + vy * n.y;
      const sc = Math.max(0, Math.min(e.len, s));
      const dist = Math.hypot(vx - e.d.x * sc, vy - e.d.y * sc);
      if (!best || dist < best.dist) best = { ei, e, n, s, t, dist };
    });
    it.edge = best;
  });

  const groups = {};
  items.forEach((it) => {
    (groups[it.edge.ei] = groups[it.edge.ei] || []).push(it);
  });

  const out = {};
  Object.values(groups).forEach((group) => {
    group.sort((a, b) => a.edge.t - b.edge.t);
    let row = [group[0]];
    const rows = [row];
    for (let i = 1; i < group.length; i++) {
      if (Math.abs(group[i].edge.t - group[i - 1].edge.t) > nn * 0.5) {
        row = [];
        rows.push(row);
      }
      row.push(group[i]);
    }
    rows.forEach((rw) => {
      const tAvg = rw.reduce((a, it) => a + it.edge.t, 0) / rw.length;
      rw.forEach((it) => {
        const { a, d } = it.edge.e;
        const n = it.edge.n;
        out[it.k] = toLL({
          x: a.x + d.x * it.edge.s + n.x * tAvg,
          y: a.y + d.y * it.edge.s + n.y * tAvg,
        });
      });
    });
  });

  return out;
}
